import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AbstractControl, FormArray, FormGroup } from "@angular/forms";
import { ICompetenciesModel, ICompetencyModel, nameofFactory } from "@ankaadia/ankaadia-shared";
import { CustomSkillSet, OccupationSkillSet, OccupationSkillType } from "@ankaadia/graphql";
import { TranslocoService } from "@jsverse/transloco";
import { FieldArrayType, FormlyFieldConfig, FormlyModule } from "@ngx-formly/core";
import { cloneDeep, isNil, remove, set } from "lodash";
import { Card } from "primeng/card";
import { Message } from "primeng/message";
import { map, Observable, switchMap } from "rxjs";
import { v4 as uuidv4 } from "uuid";
import { ProfessionService } from "../../features/profession/profession.service";
import { CvParsingHighlightService } from "../../shared/form/filled-by-ai/cv-parsing-highlight.service";
import { NewUiFormElementMapModule } from "../../shared/from-element-map/new-ui-form-element-map.module";
import { TestIdDirective } from "../../shared/test-id/test-id.directive";

function collectLeafControls(control: AbstractControl, result: AbstractControl[]): void {
  const children = (control as FormGroup | FormArray).controls;
  if (children) {
    Object.values(children).forEach((child) => collectLeafControls(child, result));
  } else {
    result.push(control);
  }
}

function hasNonEmptyValue(control: AbstractControl): boolean {
  const v = control.value;
  if (v == null) return false;
  if (typeof v === "string") return v.trim().length > 0;
  if (Array.isArray(v)) return v.length > 0;
  return true;
}

/**
 * Renders competencies of the candidate of a type declared via {@link FormlyFieldConfig.key}.
 */
@Component({
  selector: "new-ui-formly-competencies",
  templateUrl: "./new-ui-formly-competencies.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NewUiFormElementMapModule, TestIdDirective, FormlyModule, Message, Card],
})
export class NewUiFormlyCompetenciesComponent extends FieldArrayType implements OnInit {
  /**
   * Skill sets that should be rendered by this component.
   */
  sets: (OccupationSkillSet | CustomSkillSet)[];

  private readonly destroyRef = inject(DestroyRef);
  private readonly language = this.transloco.getActiveLang();

  constructor(
    private readonly professionService: ProfessionService,
    private readonly transloco: TranslocoService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly cvHighlightService: CvParsingHighlightService
  ) {
    super();
  }

  /**
   * Whenever the candidate's profession changes:
   * - load the corresponding skill sets,
   * - fix the model up so that it has no outdated skills,
   * - update the form appearance.
   */
  ngOnInit(): void {
    this.field.props ??= {};
    this.field.props.setsLoaded = false;
    (<Observable<string>>this.options.formState.profession)
      .pipe(
        switchMap((professionId) => this.loadSkillSets(professionId, this.options.formState.organizationId)),
        takeUntilDestroyed(this.destroyRef),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((sets) => {
        this.field.props.sets = this.sets = sets;
        this.field.props.setsLoaded = true;
        this.changeDetector.markForCheck();
        this.rebuildModel();
        this.rebuildForm();

        this.field.options.build(this.field);

        // Apply pending CV-parsing highlight if signalled from outside (e.g. new-ui-candidate-profile)
        const key = this.key as string;
        const shouldHighlight = this.cvHighlightService.consumePendingCompetencyHighlight(key);
        if (shouldHighlight) {
          const leafControls: AbstractControl[] = [];
          if (this.formControl instanceof FormArray) {
            for (let i = 0; i < this.formControl.length; i++) {
              collectLeafControls(this.formControl.at(i), leafControls);
            }
          }
          this.cvHighlightService.mark(leafControls.filter((c) => hasNonEmptyValue(c)));
        }

        // Evaluate children first so the parent (this field) can react to their updated visibility state
        this.field.fieldGroup?.forEach((f) => this.field.options.checkExpressions(f));
        this.field.options.checkExpressions(this.field);

        this.field.options.detectChanges(this.field);

        const doHide = this.field.hide;
        // const shouldHide = (<any>this.field.expressions).hide(this.field);
        const shouldHide = this.field.props.setsLoaded && !this.field.props.sets?.length;
        if (doHide !== shouldHide) {
          //Sometimes this hide property is not updated automatically, so we force it here...
          this.field.hide = shouldHide;
          this.field.options.detectChanges(this.field.parent);
        }
      });
  }

  /**
   * Checks whether the field is a part of the given skill set.
   */
  isInSet(field: FormlyFieldConfig, set: OccupationSkillSet | CustomSkillSet): boolean {
    const competency = <ICompetencyModel>field.model;
    return set.skills.some((x) => x.skillId === competency?.skillId);
  }

  /**
   * Returns skill sets of the given profession.
   *
   * The type of the skill sets (standard or custom, knowledge or skills) is deducted from the field's key.
   *
   * The implementation ensures that a single request is issued against the backend for all fields of this type.
   */
  private loadSkillSets(professionId: string, organizationId: string): Observable<typeof this.sets> {
    const nameOfCompetencies = nameofFactory<ICompetenciesModel>();
    return this.professionService.getProfessionSkills(professionId, organizationId, this.language).pipe(
      map(([standard, custom]) => {
        switch (this.key) {
          case nameOfCompetencies("knowledges"):
            return standard.filter((x) => x.skillType === OccupationSkillType.Knowledge);
          case nameOfCompetencies("skills"):
            return standard.filter((x) => x.skillType === OccupationSkillType.SkillCompetence);
          case nameOfCompetencies("customKnowledges"):
            return custom.filter((x) => x.skillType === OccupationSkillType.Knowledge);
          case nameOfCompetencies("customSkills"):
            return custom.filter((x) => x.skillType === OccupationSkillType.SkillCompetence);
          default:
            return null;
        }
      })
    );
  }

  /**
   * Ensures there are only those skills in the model that correspond to the selected profession.
   *
   * The same is done on the backend when saving and, perhaps, reading the candidate, but this one runs on the frontend when candidate's profession gets changed.
   * Essentially, it filters the skills from the previous profession out and creates entries for the new skills in the model.
   */
  private rebuildModel(): void {
    // if we're not editing the candidate, don't mess with the model
    if (this.props.readonly) return;

    // `model` is read-only, so direct assignment does not work
    // we would have used `assignFieldValue` but it's not exported by ngx-formly
    // so we're on our own here, trying to set it from the parent model
    if (!this.model) {
      const competencies = this.field.parent;
      if (!competencies.model) {
        // let's stop at the profile level -- it most likely isn't empty
        const profile = competencies.parent;
        set(profile.model, competencies.key, {});
      }
      set(competencies.model, this.key, []);
    }

    // The model can still be missing here (the form of a multi-edit starts without competencies), and then there is nothing to rebuild.
    if (isNil(this.model)) return;

    const newModel = <ICompetencyModel[]>this.model;
    const oldModel = cloneDeep(newModel);
    remove(newModel);

    for (const set of this.sets) {
      for (const skill of set.skills) {
        const existing = oldModel?.find((x) => x.skillId === skill.skillId);
        newModel.push(
          existing ?? { id: uuidv4(), skillId: skill.skillId, proficiency: null, additionalInformation: null }
        );
      }
    }

    this.field.options.build(this.field);
    this.field.options.detectChanges(this.field);
    this.field.options.checkExpressions(this.field);
  }

  /**
   * Updates labels and tooltips of each skill.
   *
   * This relies on the model being up-to-date, so call {@link rebuildModel} first!
   */
  private rebuildForm(): void {
    const nameOfCompetency = nameofFactory<ICompetencyModel>();
    const skills = this.sets.flatMap((x) => x.skills);

    for (const fieldGroup of this.field.fieldGroup) {
      const competency = <ICompetencyModel>fieldGroup.model;
      const skill = skills?.find((x) => x.skillId === competency?.skillId);

      const proficiency = fieldGroup.get(nameOfCompetency("proficiency"));
      if (proficiency) {
        proficiency.props ??= {};
        proficiency.props.label = skill?.preferredLabel;
        proficiency.props.tooltip = skill?.description;
      }

      const additionalInformation = fieldGroup.get(nameOfCompetency("additionalInformation"));
      if (additionalInformation) {
        additionalInformation.props ??= {};
        additionalInformation.expressions ??= {};
        const set = this.sets.find((x) => x.skills.some((y) => y.skillId === skill?.skillId));
        if (this.isCustomSkillSet(set)) {
          additionalInformation.props.requireAdditionalInformation = set.requireAdditionalInformation;
          additionalInformation.props.additionalInformationTypes = set.additionalInformationTypes;
          additionalInformation.props.label = set.additionalInformationLabel;
        } else {
          delete additionalInformation.props.requireAdditionalInformation;
          delete additionalInformation.props.additionalInformationTypes;
        }
      }
    }

    this.field.options.detectChanges(this.field);
    this.field.options.detectChanges(this.field.parent);
  }

  private isCustomSkillSet(set: OccupationSkillSet | CustomSkillSet): set is CustomSkillSet {
    return set?.__typename === "CustomSkillSet";
  }
}
